import { useState } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { PiDownloadSimple, PiMagnifyingGlass, PiPlus } from 'react-icons/pi';
import { useLocation } from 'wouter';
import dayjs from 'dayjs';
import { ClearFiltersButton } from '@/components/ClearFiltersButton';
import { useUrlDraft, useUrlState } from '@/hooks/useUrlState';
import { semanticJurisdiction } from '@/lib/helpers';
import { urlField } from '@/lib/urlState';
import { KycStateBadge } from '@/components/PayerKycBadge';
import PayerCreateForm from '@/components/PayerCreateForm';
import { useAccountsList } from '@/models/accounts';
import { useHasAnyPermission } from '@/models/permissions';
import {
  TKycStatus,
  TMondayImportCandidate,
  useImportMondayPayer,
  useMondayImportPreview,
  usePayers,
} from '@/models/payers';

const PAYERS_SCHEMA = {
  accountId: urlField.number(null, { integer: true, min: 1 }),
  kyc: urlField.enum<TKycStatus | ''>(['', 'valid', 'expired', 'missing'], ''),
  term: urlField.string(),
};

export default function PayersList() {
  const [, nav] = useLocation();
  const [state, setState, { clear, isFiltered }] = useUrlState(PAYERS_SCHEMA);
  const [term, setTerm] = useUrlDraft(state.term, (next) =>
    setState({ term: next }),
  );
  const accountId = state.accountId;
  const { data: payers = [], isLoading } = usePayers(
    accountId ?? undefined,
    state.kyc || undefined,
  );

  const canWrite = useHasAnyPermission(['payers:write', 'payers:*']);
  const [createOpen, { open: openCreate, close: closeCreate }] =
    useDisclosure(false);
  const [importOpen, { open: openImport, close: closeImport }] =
    useDisclosure(false);

  const t = term.trim().toLowerCase();
  const filtered = t
    ? payers.filter(
        (p) =>
          p.displayName.toLowerCase().includes(t) ||
          (p.accountName ?? '').toLowerCase().includes(t),
      )
    : payers;

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap">
        <Group gap="xs">
          <Title order={3}>Payers</Title>
          <Text size="sm" c="dimmed">
            {payers.length} total
          </Text>
        </Group>
        <Group gap="xs" wrap="wrap">
          <TextInput
            size="xs"
            placeholder="Search by name or account..."
            leftSection={<PiMagnifyingGlass size={14} />}
            value={term}
            onChange={(e) => setTerm(e.currentTarget.value)}
            w={220}
          />
          <Select
            size="xs"
            placeholder="KYC status"
            clearable
            data={[
              { value: 'valid', label: 'Valid' },
              { value: 'expired', label: 'Expired' },
              { value: 'missing', label: 'Missing' },
            ]}
            value={state.kyc || null}
            onChange={(v) => setState({ kyc: (v ?? '') as typeof state.kyc })}
            w={130}
          />
          <ClearFiltersButton visible={isFiltered} onClick={clear} />
          {canWrite && (
            <>
              <Button
                size="xs"
                variant="light"
                leftSection={<PiDownloadSimple size={14} />}
                onClick={openImport}
              >
                Import from Monday
              </Button>
              <Button
                size="xs"
                leftSection={<PiPlus size={14} />}
                onClick={openCreate}
              >
                Create Payer
              </Button>
            </>
          )}
        </Group>
      </Group>

      {isLoading ? (
        <Loader />
      ) : filtered.length === 0 ? (
        <Paper withBorder p="md">
          <Text c="dimmed" size="sm">
            No payers found.
          </Text>
        </Paper>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Country</Table.Th>
              <Table.Th>Account</Table.Th>
              <Table.Th>KYC</Table.Th>
              <Table.Th>Created</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.map((p) => (
              <Table.Tr
                key={p.id}
                style={{ cursor: 'pointer' }}
                onClick={() => nav(`~/payers/${p.id}`)}
              >
                <Table.Td fw={500}>{p.displayName}</Table.Td>
                <Table.Td>{p.clientType}</Table.Td>
                <Table.Td>{semanticJurisdiction(p.countryCode)}</Table.Td>
                <Table.Td>{p.accountName ?? '—'}</Table.Td>
                <Table.Td>
                  <KycStateBadge kycExpiresAt={p.kycExpiresAt} size="xs" />
                </Table.Td>
                <Table.Td>
                  {p.createdAt ? dayjs(p.createdAt).format('DD.MM.YYYY') : '—'}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Modal
        opened={createOpen}
        onClose={closeCreate}
        title="Create Payer"
        size="lg"
      >
        <PayerCreateForm
          accountId={accountId ?? undefined}
          onCreated={(payer) => {
            closeCreate();
            nav(`~/payers/${payer.id}`);
          }}
          onCancel={closeCreate}
        />
      </Modal>

      <ImportMondayModal opened={importOpen} onClose={closeImport} />
    </Stack>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="sm">{value || '—'}</Text>
    </div>
  );
}

function ImportMondayModal({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  const [, nav] = useLocation();
  const importMonday = useImportMondayPayer();
  const preview = useMondayImportPreview();
  const [itemId, setItemId] = useState<number | string>('');
  const [accountSearch, setAccountSearch] = useState('');
  const [accountId, setAccountId] = useState<string | null>(null);
  const [candidate, setCandidate] = useState<TMondayImportCandidate | null>(
    null,
  );
  const [emailConflictAck, setEmailConflictAck] = useState(false);
  const { data: accounts } = useAccountsList({
    search: accountSearch || undefined,
    view: 'all',
    pageSize: 50,
  });

  const handleClose = () => {
    setCandidate(null);
    setItemId('');
    setAccountId(null);
    setEmailConflictAck(false);
    onClose();
  };

  const loadPreview = () => {
    if (!itemId || !accountId) return;
    preview.mutate(
      { mondayContactId: Number(itemId), accountId: Number(accountId) },
      {
        onSuccess: (next) => {
          setEmailConflictAck(false);
          setCandidate(next);
        },
        onError: (err) =>
          notifications.show({ color: 'red', message: String(err) }),
      },
    );
  };

  const confirmImport = () => {
    if (!candidate || !accountId) return;
    importMonday.mutate(
      {
        mondayKycItemId: candidate.mondayKycItemId,
        accountId: Number(accountId),
        mondayContactId: candidate.mondayContactId ?? Number(itemId),
        acknowledgeEmailConflict: emailConflictAck,
      },
      {
        onSuccess: (payer) => {
          handleClose();
          nav(`~/payers/${payer.id}`);
        },
        onError: (err) =>
          notifications.show({ color: 'red', message: String(err) }),
      },
    );
  };

  const emailConflict = candidate?.verifiedEmailStatus === 'conflicting-label';
  // The missing-email case has its own alert explaining why it is fatal.
  const missingBesidesEmail = (candidate?.missing ?? []).filter(
    (m) => m !== 'verified email',
  );

  const canImport =
    candidate &&
    candidate.missing.length === 0 &&
    !candidate.existingPayerId &&
    (!emailConflict || emailConflictAck);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Import from Monday"
      size="lg"
    >
      <Stack gap="sm">
        <Group grow align="flex-end">
          <NumberInput
            label="Monday Contact Item ID"
            description="Linked KYC records are looked up automatically"
            value={itemId}
            onChange={(v) => {
              setItemId(v);
              setCandidate(null);
            }}
          />
          <Select
            label="Account"
            placeholder="Search account..."
            searchable
            data={(accounts?.rows ?? []).map((a) => ({
              value: String(a.id),
              label: a.name,
            }))}
            searchValue={accountSearch}
            onSearchChange={setAccountSearch}
            value={accountId}
            onChange={(v) => {
              setAccountId(v);
              setCandidate(null);
            }}
          />
        </Group>

        {candidate && (
          <Paper withBorder p="sm">
            <Stack gap="sm">
              <Group gap="xs">
                <Text fw={600} fz="sm">
                  {candidate.clientDetails.fullName || candidate.itemName}
                </Text>
                <Badge
                  size="sm"
                  variant="light"
                  color={candidate.source === 'sumsub' ? 'teal' : 'blue'}
                >
                  {candidate.source === 'sumsub'
                    ? 'KYC (SumSub)'
                    : 'KYC (Manual)'}
                </Badge>
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
                <Fact
                  label="Document Number"
                  value={candidate.clientDetails.documentNumber}
                />
                <Fact
                  label="Nationality"
                  value={candidate.clientDetails.nationality}
                />
                <Fact label="Country" value={candidate.countryCode} />
                <Fact
                  label="Legal Address"
                  value={candidate.clientDetails.legalAddress}
                />
                <Fact
                  label="ID Valid Until"
                  value={
                    candidate.docValidUntil
                      ? dayjs(candidate.docValidUntil).format('DD.MM.YYYY')
                      : '—'
                  }
                />
                <Fact
                  label="KYC Will Expire"
                  value={dayjs(candidate.kycExpiresAt).format('DD.MM.YYYY')}
                />
                <Fact
                  label="Verified Email"
                  value={candidate.verifiedEmail ?? '—'}
                />
              </SimpleGrid>
              <Fact
                label={`Documents (${candidate.documents.length})`}
                value={
                  candidate.documents.map((d) => d.name).join(', ') ||
                  'None found'
                }
              />
              {candidate.existingPayerId && (
                <Alert color="blue">
                  <Text fz="sm">
                    This KYC item was already imported —{' '}
                    <Anchor
                      fz="sm"
                      onClick={() => {
                        handleClose();
                        nav(`~/payers/${candidate.existingPayerId}`);
                      }}
                    >
                      open the existing payer
                    </Anchor>
                    .
                  </Text>
                </Alert>
              )}
              {!candidate.verifiedEmail && (
                <Alert color="red">
                  <Text fz="sm">
                    This KYC record carries no verified email address, so a
                    payer imported from it could never be invoiced. The KYC
                    boards are the only source of an invoice address, and it
                    cannot be edited afterwards. Fill in "1.1 Email
                    verification" on the Monday item, then preview again.
                  </Text>
                </Alert>
              )}
              {emailConflict && (
                <Alert color="orange">
                  <Stack gap="xs">
                    <Text fz="sm">
                      The "1.1 Email verification" column holds two different
                      addresses. Invoices would be sent to the address, not the
                      label — confirm that is correct, or fix the Monday item.
                    </Text>
                    <Text fz="sm">
                      Address: <b>{candidate.verifiedEmail}</b>
                      <br />
                      Label: <b>{candidate.conflictingEmailLabel}</b>
                    </Text>
                    <Checkbox
                      checked={emailConflictAck}
                      onChange={(e) =>
                        setEmailConflictAck(e.currentTarget.checked)
                      }
                      label={`Invoice ${candidate.verifiedEmail}`}
                    />
                  </Stack>
                </Alert>
              )}
              {missingBesidesEmail.length > 0 && (
                <Alert color="orange">
                  <Text fz="sm">
                    Cannot import — missing required details:{' '}
                    {missingBesidesEmail.join(', ')}. Complete them on the
                    Monday item first.
                  </Text>
                </Alert>
              )}
            </Stack>
          </Paper>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose}>
            Cancel
          </Button>
          {!candidate ? (
            <Button
              loading={preview.isPending}
              disabled={!itemId || !accountId}
              onClick={loadPreview}
            >
              Preview
            </Button>
          ) : (
            <Button
              loading={importMonday.isPending}
              disabled={!canImport}
              onClick={confirmImport}
            >
              Confirm Import
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
